/**
 * Main Application Component
 * 
 * Root component that sets up routing, authentication, and global providers
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/utils/auth';
import { Toaster } from '@/components/ui/sonner';
import apiClient from '@/utils/api';

// Pages
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Apply from '@/pages/Apply';
import AuthCallback from '@/pages/AuthCallback';
import ResetPassword from '@/pages/ResetPassword';

// Member Pages
import MemberDashboardOverview from '@/pages/MemberDashboardOverview';
import MemberPayments from '@/pages/MemberPayments';
import MemberDocuments from '@/pages/MemberDocuments';
import MemberNotifications from '@/pages/MemberNotifications';
import MemberProfile from '@/pages/MemberProfile';
import MemberProfileEdit from '@/pages/MemberProfileEdit';
import MemberBeneficiaries from '@/pages/MemberBeneficiaries';
import MemberProviders from '@/pages/MemberProviders';

// Admin Pages
import AdminDashboard from '@/pages/AdminDashboard';
import AdminMembers from '@/pages/AdminMembers';
import AdminApprovals from '@/pages/AdminApprovals';
import AdminPayments from '@/pages/AdminPayments';
import AdminDocuments from '@/pages/AdminDocuments';
import AdminReports from '@/pages/AdminReports';
import AdminUsageReport from '@/pages/AdminUsageReport';
import AdminSettings from '@/pages/AdminSettings';
import AdminProfile from '@/pages/AdminProfile';
import AdminProfileEdit from '@/pages/AdminProfileEdit';
import AdminMemberPayments from '@/pages/AdminMemberPayments';
import AdminGenerateInvoice from '@/pages/AdminGenerateInvoice';
import AdminUsers from '@/pages/AdminUsers';
import AdminBeneficiaries from '@/pages/AdminBeneficiaries';
import AdminProviders from '@/pages/AdminProviders';
import ProtectedRoute from '@/components/ProtectedRoute';
import MilestoneCelebration from '@/components/MilestoneCelebration';
import SessionTimeout from '@/components/SessionTimeout';
import ImpersonationBanner from '@/components/ImpersonationBanner';

import '@/App.css';

/**
 * Router component that handles authentication callback detection
 */
function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check for session_id in URL hash (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Member Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MemberDashboardOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/payments"
        element={
          <ProtectedRoute>
            <MemberPayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/documents"
        element={
          <ProtectedRoute>
            <MemberDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/notifications"
        element={
          <ProtectedRoute>
            <MemberNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/profile"
        element={
          <ProtectedRoute>
            <MemberProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/profile/edit"
        element={
          <ProtectedRoute>
            <MemberProfileEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/beneficiaries"
        element={
          <ProtectedRoute>
            <MemberBeneficiaries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/providers"
        element={
          <ProtectedRoute>
            <MemberProviders />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members"
        element={
          <ProtectedRoute requireAdmin>
            <AdminMembers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/approvals"
        element={
          <ProtectedRoute requireAdmin>
            <AdminApprovals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/beneficiaries"
        element={
          <ProtectedRoute requireAdmin>
            <AdminBeneficiaries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute requireAdmin>
            <AdminPayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/providers"
        element={
          <ProtectedRoute requireAdmin>
            <AdminProviders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/documents"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute requireAdmin>
            <AdminReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usage"
        element={
          <ProtectedRoute requireAdmin>
            <AdminUsageReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requireAdmin>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requireAdmin>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute requireAdmin>
            <AdminProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/profile/edit"
        element={
          <ProtectedRoute requireAdmin>
            <AdminProfileEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members/:memberId/payments"
        element={
          <ProtectedRoute requireAdmin>
            <AdminMemberPayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members/:memberId/generate-invoice"
        element={
          <ProtectedRoute requireAdmin>
            <AdminGenerateInvoice />
          </ProtectedRoute>
        }
      />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Set document title and favicon from branding settings
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await apiClient.get('/settings/branding');
        const appName = response.data?.app_name || 'MemberHub';
        document.title = appName;

        // Apply custom favicon if configured
        const faviconUrl = response.data?.favicon_url;
        if (faviconUrl) {
          let link = document.querySelector("link[rel='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = faviconUrl;

          // Apple touch icon for "Add to Home Screen" on iOS
          let apple = document.querySelector("link[rel='apple-touch-icon']");
          if (!apple) {
            apple = document.createElement('link');
            apple.rel = 'apple-touch-icon';
            document.head.appendChild(apple);
          }
          apple.href = faviconUrl;
        }

        // Branded PWA title + dynamic web app manifest
        const setMeta = (name, content) => {
          let m = document.querySelector(`meta[name='${name}']`);
          if (!m) {
            m = document.createElement('meta');
            m.name = name;
            document.head.appendChild(m);
          }
          m.content = content;
        };
        setMeta('apple-mobile-web-app-title', appName);
        setMeta('application-name', appName);

        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        let manifest = document.querySelector("link[rel='manifest']");
        if (!manifest) {
          manifest = document.createElement('link');
          manifest.rel = 'manifest';
          document.head.appendChild(manifest);
        }
        manifest.href = `${backendUrl}/api/settings/manifest.json`;
      } catch (error) {
        // Fallback to default title
        document.title = 'MemberHub';
      }
    };
    loadBranding();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <MilestoneCelebration />
        <SessionTimeout />
        <ImpersonationBanner />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
